import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Grid,
  IconButton,
  Typography,
  Box,
  useTheme,
} from '@mui/material';
import { Button } from '@material-ui/core';
import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';
import useMediaQuery from '@mui/material/useMediaQuery';
import FundFamily from './FundFamily';
import { PlanContext } from '../../../context/PlanContext';
import { FundType, TierType } from '../../../constants';
import sortCoreFundFamilyKeys from '../../../helpers/sortCoreFundFamilyKeys';

const FundPickerModal = ({
  open,
  onClose,
  fundData,
  selectedFunds,
  setSelectedFunds,
  modalTitle,
  setFormData,
  onUpdateAllocation,
  fundType,
}) => {
  const [fundsSelected, setFundsSelected] = useState([]);
  const { userPortfolio } = useContext(PlanContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    setFundsSelected(selectedFunds || []);
  }, [selectedFunds, open]);

  const handleToggleFundSelection = (fundKey) => {
    setFundsSelected((prevSelected) => (prevSelected?.includes(fundKey)
      ? prevSelected.filter((key) => key !== fundKey)
      : [...prevSelected, fundKey]));
  };

  const handleSaveSelection = () => {
    setSelectedFunds(fundsSelected);
    setFormData((prevData) => {
      const updatedFormData = { ...prevData };
      // remove the funds that are not selected
      Object.keys(updatedFormData).forEach((fundFamilyCode) => {
        if (!fundsSelected.includes(fundFamilyCode)) {
          delete updatedFormData[fundFamilyCode];
        }
      });
      onUpdateAllocation(updatedFormData);
      return updatedFormData;
    });
    onClose();
  };

  const handleDialogClick = (event) => {
    event.stopPropagation();
  };

  const filteredFundData = Object.keys(fundData).reduce((acc, key) => {
    if (key !== 'opportunity-fund' || userPortfolio.tier === TierType.DIAMOND) {
      acc[key] = fundData[key];
    }
    return acc;
  }, {});

  const fundFamilyKeys = fundType === FundType.CORE
    ? sortCoreFundFamilyKeys(filteredFundData)
    : Object.keys(filteredFundData);
  const totalColumns = isMobile ? 2 : 4;
  const totalFunds = fundFamilyKeys.length;
  const noOfElementsInLastRow = totalFunds % totalColumns === 0 ? totalColumns : totalFunds % totalColumns;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          maxWidth: '120vh',
          maxHeight: '80vh',
          overflow: 'auto',
        },
      }}
      onClick={handleDialogClick}
    >
      <DialogTitle
        sx={{
          paddingTop: '3%',
          paddingRight: { sm: '15%', md: '3%' },
          textAlign: { sm: 'center', md: 'left' },
        }}
      >
        <Typography variant="h1Large">{modalTitle}</Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={(dialogTheme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: dialogTheme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container sx={{ paddingTop: '20px' }}>
          {fundFamilyKeys
            .map((fundKey, index) => {
              const fund = filteredFundData[fundKey];
              const isSelected = fundsSelected?.includes(fundKey);
              const isLastColumn = (index + 1) % totalColumns === 0;
              const isLastRow = index >= (totalFunds - noOfElementsInLastRow);
              const margin = ((12 - (noOfElementsInLastRow * 3)) / 12) * 100;

              let marginValue = '';
              if (isMobile && isLastRow && parseInt(noOfElementsInLastRow, 10) === 1) {
                marginValue = '0 25%';
              } else if (!isMobile && isLastRow) {
                if (parseInt(index, 10) === parseInt(totalFunds - noOfElementsInLastRow, 10)) {
                  marginValue = `0 0 0 ${margin / 2}%`; // First item in the last row
                } else if (index === totalFunds + 1) {
                  marginValue = `0 ${margin / 2}% 0 0`; // Last item in the last row
                }
              }

              return (
                <Grid
                  item
                  xs={6}
                  sm={3}
                  key={fundKey}
                  sx={{
                    borderRight: isLastColumn || index === totalFunds - 1 ? 'none' : '1px solid #efefef',
                    borderBottom: isLastRow ? 'none' : '1px solid #efefef',
                    margin: marginValue,
                  }}
                >
                  <Box sx={{ padding: 1, height: '100%' }}>
                    <FundFamily
                      family={fund}
                      selectorState={isSelected}
                      fundType={fundType}
                      onClick={() => handleToggleFundSelection(fundKey)}
                    />
                  </Box>
                </Grid>
              );
            })}
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: 'center', // Center-align the content
        }}
      >
        <Button
          color="secondary"
          variant="contained"
          style={{
            marginBottom: '10px',
            background: '#CFB72E',
          }}
          onClick={handleSaveSelection}
        >
          Save Selection
        </Button>
      </DialogActions>
    </Dialog>
  );
};

FundPickerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  fundData: PropTypes.objectOf(
    PropTypes.shape({
      general: PropTypes.shape({
        title: PropTypes.string.isRequired,
      }),
      fields: PropTypes.shape({
        logo: PropTypes.shape({
          url: PropTypes.string,
          dimensions: PropTypes.shape({
            width: PropTypes.number,
            height: PropTypes.number,
          }),
        }),
        subTitle: PropTypes.string,
      }),
    }).isRequired,
  ).isRequired,
  selectedFunds: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedFunds: PropTypes.func.isRequired,
  modalTitle: PropTypes.string.isRequired,
  setFormData: PropTypes.func.isRequired,
  onUpdateAllocation: PropTypes.func.isRequired,
  fundType: PropTypes.number.isRequired,
};

export default FundPickerModal;
